// Selectable colour profiles. Each one re-tints the chrome + accent (see the [data-palette] blocks in
// tokens.css); the neutral canvas/surfaces come from the light|dark theme, so a palette works on either.
// `swatch` is the deep chrome colour, `accent` the light-mode accent — both used for the settings/first-run
// previews, and lib/themes.test.ts checks them against tokens.css so the previews never drift from the CSS.
// 'indigo' (#12132e) is the default. The ids themselves live in @technohull/shared (PALETTE_IDS) so the
// server schema accepts exactly these.

import { PALETTE_IDS, type PaletteId } from '@technohull/shared';

export type { PaletteId };

/** 'brand' profiles pair with a logo colourway; 'more' are the extra neutral and wood tones. */
export type PaletteGroup = 'brand' | 'more';

export interface PaletteDef { label: string; blurb: string; swatch: string; accent: string; group: PaletteGroup }

export const PALETTES: Record<PaletteId, PaletteDef> = {
  // ── ROLODEX brand palettes (from ROLODEX_color_palettes.md) — pair with the matching logo ──
  indigo: { label: 'Indigo', blurb: 'Deep indigo - the default', swatch: '#12132e', accent: '#4b4e8f', group: 'brand' },
  blue: { label: 'Blue', blurb: 'Electric royal blue', swatch: '#0f0f42', accent: '#2e2dae', group: 'brand' },
  red: { label: 'Red', blurb: 'Bright crimson', swatch: '#5e1219', accent: '#b91d27', group: 'brand' },
  green: { label: 'Green', blurb: 'Vivid forest green', swatch: '#0c2a27', accent: '#0f8a41', group: 'brand' },
  gold: { label: 'Gold', blurb: 'Antique gold', swatch: '#3b222c', accent: '#8f7a2c', group: 'brand' },
  silver: { label: 'Silver', blurb: 'Mono greyscale', swatch: '#141414', accent: '#5f5f5f', group: 'brand' },
  // ── extra profiles ──
  teal: { label: 'Teal', blurb: 'Marine teal', swatch: '#0f2b30', accent: '#158393', group: 'more' },
  slate: { label: 'Slate', blurb: 'Cool blue-grey', swatch: '#1a2130', accent: '#4d6a86', group: 'more' },
  graphite: { label: 'Graphite', blurb: 'Neutral charcoal', swatch: '#1c1d21', accent: '#5f6470', group: 'more' },
  burgundy: { label: 'Burgundy', blurb: 'Deep wine', swatch: '#2a1220', accent: '#9c4256', group: 'more' },
  forest: { label: 'Forest', blurb: 'Deep green', swatch: '#10241a', accent: '#3f7f5a', group: 'more' },
  plum: { label: 'Plum', blurb: 'Deep aubergine', swatch: '#1f1226', accent: '#7b3f8c', group: 'more' },
  teak: { label: 'Teak', blurb: 'Warm deck brown', swatch: '#2a1a10', accent: '#8a5730', group: 'more' },
  olive: { label: 'Olive', blurb: 'Muted olive green', swatch: '#1b1d0f', accent: '#626527', group: 'more' },
  sand: { label: 'Sand', blurb: 'Warm neutral', swatch: '#262019', accent: '#76664e', group: 'more' },
};

export const PALETTE_ORDER: PaletteId[] = [...PALETTE_IDS];

/** resolve a (possibly untrusted / legacy) stored value to a valid palette id. */
export function paletteId(v: string | undefined | null): PaletteId {
  return v && Object.hasOwn(PALETTES, v) ? (v as PaletteId) : 'indigo';
}
